import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { GET, PUT, httpOk } from 'helpers/api'
import { formatMoney, uploadUrl } from 'helpers/helpers'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  TAYYOR MAHSULOT ZAPASI — MIN DARAJA
 *  2026-08-24
 *
 *  Egasining talabi: omborda tayyor mahsulot (kamar, tarnov, truba,
 *  germetika, samarez) zapasini ushlab turish; kamayganda ogohlantirish;
 *  omborchi hamkorga zakaz berishi.
 *
 *  ═══ EKRAN NEGA SHUNDAY QURILGAN ═══
 *
 *  1) DARAJA «KUN» DA BELGILANADI, dona da emas. Egasi «minimum 500 ta»
 *     degan edi; 90 kunlik haqiqiy sarf o'lchandi:
 *       · T.kamar 3b (tosh) kuniga 119.6 dona → 500 ta ATIGI 4 KUN;
 *       · Chiganok 4b kuniga 2.4 dona        → 500 ta 210 KUN (o'lik pul);
 *       · Samarez KILOGRAMMDA, tarnov METRDA sotiladi — «500 ta» ma'nosiz.
 *     Dastur tavsiya beradi (kunlik sarf × muddat), egasi tasdiqlaydi yoki
 *     o'z raqamini yozadi — «qo'lda belgilash» talabi shunday bajariladi.
 *
 *  2) OGOHLANTIRISH FAQAT NAZORATGA OLINGAN mahsulotda. Sinovda: nazoratsiz
 *     ham «qoldiq 0 → qizil» qilganda 198 mahsulotdan 79 tasi qizil chiqdi —
 *     ko'pchiligi buyurtmaga kesiladigan Burchak/Betlik (kuniga 0.04 dona).
 *     To'la qizil ekran haqiqiy ogohlantirishni ko'rinmas qiladi.
 *
 *  3) Nazoratsiz qatorlar SOTILISH TEZLIGI bo'yicha saralanadi — ya'ni
 *     «nazoratga olishga eng arziydigani» birinchi turadi va egasi ro'yxatni
 *     varaqlamasdan kamar/tarnovni topib belgilaydi.
 *
 *  ⚠️ SOTUVNI BLOKLASH BU EKRANDA YO'Q va ataylab yo'q: 90 kunda sotilgan
 *     104 ta tayyor mahsulotning 46 tasi hozir nol qoldiq ko'rsatadi va ular
 *     savdoning 35.5% ini beradi. Blok bugun yoqilsa savdoning uchdan biri
 *     to'xtaydi. Avval inventarizatsiya bilan qoldiq faktga to'g'rilanadi.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/* Holat ranglari. Bootstrap 5.2.3 — `bg-*-subtle` YO'Q, shuning uchun
   fon qo'lda beriladi; qorong'i mavzu CSS o'zgaruvchilari orqali. */
const STATUS = {
	out:       { label: 'Тугаган',      color: 'var(--warn-ink, #dc3545)', bg: 'var(--warn-ink-bg, #fce4e4)' },
	low:       { label: 'Кам қолди',    color: '#a16207',                  bg: 'var(--cat-wr-bg, #fff3cd)' },
	ok:        { label: 'Етарли',       color: 'var(--ok-mid, #198754)',   bg: 'var(--ok-mid-bg, #e8f5e9)' },
	untracked: { label: 'Назоратда эмас', color: 'var(--dk-text-2, #6c757d)', bg: 'var(--an-chip-bg, #f1f3f7)' },
}

const STATUS_FILTERS = [
	{ id: '', label: 'Ҳаммаси' },
	{ id: 'out', label: 'Тугаган' },
	{ id: 'low', label: 'Кам қолди' },
	{ id: 'ok', label: 'Етарли' },
	{ id: 'untracked', label: 'Назоратда эмас' },
]

/** Son — mahsulotning o'z birligida. Dona butun, metr/kg kasrli bo'lishi mumkin. */
const son = v => {
	const n = Number(v) || 0
	return Number.isInteger(n) ? formatMoney(n, 0) : formatMoney(Math.round(n * 10) / 10, 1)
}

export default function StockLevels() {
	const { t } = useTranslation()

	const [rows, setRows] = useState([])
	const [summary, setSummary] = useState({})
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)

	const [search, setSearch] = useState('')
	const [status, setStatus] = useState('')
	const [onlyTracked, setOnlyTracked] = useState(false)
	const [page, setPage] = useState(0)
	const SIZE = 50

	// Tahrirlanayotgan qator: { id, is_stock_item, min_quantity, order_qty, lead_days }
	const [edit, setEdit] = useState(null)
	const [saving, setSaving] = useState(false)

	const load = useCallback(async (p = 0, opts = {}) => {
		setLoading(true)
		const q = {
			page: p, size: SIZE,
			search: opts.search !== undefined ? opts.search : search,
			status: opts.status !== undefined ? opts.status : status,
			only_tracked: opts.onlyTracked !== undefined ? opts.onlyTracked : onlyTracked,
		}
		const res = await GET('/stock/levels', q, { loader: false })
		setLoading(false)
		if (httpOk(res)) {
			setRows(res.data.data || [])
			setTotal(res.data.total || 0)
			setSummary(res.data.summary || {})
		}
	}, [search, status, onlyTracked])

	useEffect(() => { load(0) }, []) // eslint-disable-line react-hooks/exhaustive-deps

	function applyFilter(patch) {
		if (patch.status !== undefined) setStatus(patch.status)
		if (patch.onlyTracked !== undefined) setOnlyTracked(patch.onlyTracked)
		setPage(0)
		load(0, patch)
	}

	function startEdit(r) {
		setEdit({
			id: r.id,
			name: r.name,
			unit: r.unit_name,
			is_stock_item: r.is_stock_item,
			// Bo'sh qoldirilsa dastur tavsiyani ishlatadi — shuning uchun
			// mavjud qiymat bo'lmasa maydon BO'SH turadi, tavsiya esa yonida
			// ko'rsatiladi (placeholder). Tavsiyani darrov yozib qo'ysak
			// «qo'lda belgilangan» bilan «hisoblangan» farqi yo'qoladi.
			min_quantity: r.min_quantity ?? '',
			order_qty: r.order_qty ?? '',
			lead_days: r.lead_days ?? '',
			suggested_min: r.suggested_min,
			suggested_order: r.suggested_order,
			daily_rate: r.daily_rate,
			min_days: r.min_days,
		})
	}

	async function saveEdit() {
		if (!edit) return
		setSaving(true)
		const res = await PUT(`/stock/levels/${edit.id}`, {
			is_stock_item: !!edit.is_stock_item,
			// Bo'sh satr -> null (dastur tavsiyaga qaytadi)
			min_quantity: edit.min_quantity === '' ? null : Number(edit.min_quantity),
			order_qty: edit.order_qty === '' ? null : Number(edit.order_qty),
			lead_days: edit.lead_days === '' ? null : Number(edit.lead_days),
		}, { loader: false })
		setSaving(false)
		if (httpOk(res)) {
			toast.success(t('saved'))
			setEdit(null)
			load(page)
		}
	}

	/** «Nazoratga ol» — bir bosishda: belgilaydi va tavsiyani qabul qiladi. */
	async function quickTrack(r) {
		const res = await PUT(`/stock/levels/${r.id}`, {
			is_stock_item: true,
			min_quantity: null,          // tavsiya ishlatiladi
			order_qty: null,
			lead_days: r.lead_days ?? null,
		}, { loader: false })
		if (httpOk(res)) { toast.success(t('saved')); load(page) }
	}

	const pages = useMemo(() => Math.ceil(total / SIZE), [total])

	return (
		<div className="an-page">
			<div className="page-header d-flex justify-content-between align-items-center mb-3">
				<h4 className="mb-0">{t('stock_levels')}</h4>
				<div className="d-flex gap-2">
					{[
						{ k: 'out', n: summary.out, ...STATUS.out },
						{ k: 'low', n: summary.low, ...STATUS.low },
						{ k: 'tracked', n: summary.tracked, label: 'Назоратда', color: 'var(--dk-text-2, #495057)', bg: 'var(--an-chip-bg, #f1f3f7)' },
					].map(c => (
						<button key={c.k} type="button"
							className="border-0 px-3 py-2 rounded d-flex align-items-center gap-2"
							style={{ background: c.bg, color: c.color, cursor: c.k === 'tracked' ? 'default' : 'pointer' }}
							onClick={() => c.k !== 'tracked' && applyFilter({ status: status === c.k ? '' : c.k })}>
							<span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em' }}>{c.label}</span>
							<span className="fw-bold" style={{ fontSize: 17 }}>{c.n ?? 0}</span>
						</button>
					))}
				</div>
			</div>

			{/* ── Filtr ── */}
			<div className="card mb-3">
				<div className="card-body py-2 px-3">
					<div className="d-flex align-items-center gap-2 flex-wrap">
						<input type="text" className="form-control form-control-sm" style={{ maxWidth: 240 }}
							placeholder={t('search') + '...'} value={search}
							onChange={e => setSearch(e.target.value)}
							onKeyDown={e => { if (e.key === 'Enter') { setPage(0); load(0) } }} />
						<button className="btn btn-sm btn-success" onClick={() => { setPage(0); load(0) }}>
							{loading ? <span className="spinner-border spinner-border-sm" /> : <i className="uil-search" />}
						</button>

						<div className="btn-group btn-group-sm ms-2">
							{STATUS_FILTERS.map(f => (
								<button key={f.id} type="button"
									className={'btn ' + (status === f.id ? 'btn-primary' : 'btn-outline-secondary')}
									onClick={() => applyFilter({ status: f.id })}>{f.label}</button>
							))}
						</div>

						<div className="form-check form-switch mb-0 ms-2">
							<input className="form-check-input" type="checkbox" id="onlyTr"
								checked={onlyTracked}
								onChange={e => applyFilter({ onlyTracked: e.target.checked })} />
							<label className="form-check-label text-muted" htmlFor="onlyTr" style={{ fontSize: 12 }}>
								Фақат назоратдагилар
							</label>
						</div>

						<span className="text-muted ms-auto" style={{ fontSize: 12 }}>
							{t('total')}: <b>{total}</b>
						</span>
					</div>
				</div>
			</div>

			<div className="card">
				<div className="card-body p-0">
					<div className="table-responsive">
						<table className="table table-hover mb-0 align-middle">
							<thead>
								<tr>
									<th style={{ width: 46 }} className="text-center">{t('image2')}</th>
									<th>{t('product_name')}</th>
									<th style={{ width: 90 }}>{t('unit')}</th>
									<th style={{ width: 100 }} className="text-end">{t('balance2')}</th>
									{/* «Кунлик сарф» — 90 kunlik haqiqiy sotuvdan */}
									<th style={{ width: 100 }} className="text-end">Кунлик сарф</th>
									<th style={{ width: 100 }} className="text-end">Неча кунга</th>
									<th style={{ width: 120 }} className="text-end">Мин. даража</th>
									<th style={{ width: 130 }} className="text-center">{t('status')}</th>
									<th style={{ width: 130 }} className="text-center">{t('actions')}</th>
								</tr>
							</thead>
							<tbody>
								{rows.length === 0 &&
									<tr><td colSpan={9} className="text-center text-muted py-4">{t('list_empty')}</td></tr>
								}
								{rows.map(r => {
									const st = STATUS[r.status] || STATUS.untracked
									return (
										<tr key={r.id}>
											<td className="text-center">
												{r.image
													? <img src={`${uploadUrl}/products/${r.image}`} alt=""
														style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 6 }} />
													: <div style={{
														width: 34, height: 34, borderRadius: 6,
														background: 'var(--ca-track, #eef0f4)', display: 'inline-flex',
														alignItems: 'center', justifyContent: 'center',
													}}>
														<i className="uil-image" style={{ color: 'var(--dk-text-2, #adb5bd)', fontSize: 15 }} />
													</div>
												}
											</td>
											<td>
												<div style={{ fontSize: 13.5 }}>{r.name}</div>
												<div className="text-muted" style={{ fontSize: 11 }}>
													{r.product_type_name}
													{r.on_order > 0 &&
														<span className="ms-2" style={{ color: '#0d6efd' }}>
															· заказда {son(r.on_order)}
														</span>}
												</div>
											</td>
											<td className="text-muted" style={{ fontSize: 12 }}>{r.unit_name || '—'}</td>
											<td className="text-end fw-bold" style={{ fontSize: 13.5 }}>{son(r.balance)}</td>
											<td className="text-end text-muted" style={{ fontSize: 12.5 }}>
												{r.daily_rate > 0 ? son(r.daily_rate) : '—'}
											</td>
											<td className="text-end" style={{ fontSize: 12.5 }}>
												{r.cover_days === null
													? <span className="text-muted">—</span>
													: <span style={{ color: r.cover_days < r.min_days ? st.color : 'inherit' }}>
														{r.cover_days} кун
													</span>}
											</td>
											<td className="text-end" style={{ fontSize: 12.5 }}>
												{r.threshold === null
													? <span className="text-muted">—</span>
													: <>
														{son(r.threshold)}
														{/* Qo'lda yozilgani vs hisoblangani — farqi ko'rinib tursin */}
														<div className="text-muted" style={{ fontSize: 10.5 }}>
															{r.min_quantity !== null ? 'қўлда' : `${r.min_days} кунлик`}
														</div>
													</>}
											</td>
											<td className="text-center">
												<span className="d-inline-block px-2 py-1 rounded"
													style={{ background: st.bg, color: st.color, fontSize: 11, fontWeight: 700 }}>
													{st.label}
												</span>
											</td>
											<td className="text-center">
												{!r.is_stock_item && r.daily_rate > 0 &&
													<button className="btn btn-sm btn-outline-success me-1"
														title={`Мин. даража ${son(r.suggested_min)} қилиб назоратга олинади`}
														onClick={() => quickTrack(r)}>
														<i className="uil-check" /> Назоратга ол
													</button>
												}
												<button className="btn btn-sm btn-outline-secondary"
													onClick={() => startEdit(r)}>
													<i className="uil-edit-alt" />
												</button>
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{pages > 1 &&
				<div className="d-flex justify-content-center gap-1 mt-3">
					<button className="btn btn-sm btn-outline-secondary" disabled={page === 0}
						onClick={() => { setPage(page - 1); load(page - 1) }}>
						<i className="uil-angle-left" />
					</button>
					<span className="btn btn-sm disabled">{page + 1} / {pages}</span>
					<button className="btn btn-sm btn-outline-secondary" disabled={page + 1 >= pages}
						onClick={() => { setPage(page + 1); load(page + 1) }}>
						<i className="uil-angle-right" />
					</button>
				</div>
			}

			{/* ═══ Tahrirlash ═══ */}
			{edit &&
				<>
					<div className="modal-backdrop fade show" onClick={() => setEdit(null)} />
					<div className="modal fade show d-block" tabIndex="-1" role="dialog">
						<div className="modal-dialog modal-dialog-centered">
							<div className="modal-content">
								<div className="modal-header">
									<h6 className="modal-title">{edit.name}</h6>
									<button type="button" className="btn-close" onClick={() => setEdit(null)} />
								</div>
								<div className="modal-body">
									<div className="alert alert-light border py-2 mb-3" style={{ fontSize: 12.5 }}>
										Кунлик сарф <b>{son(edit.daily_rate)} {edit.unit}</b> (90 кунлик сотувдан).
										{edit.suggested_min !== null &&
											<> Тавсия: <b>{son(edit.suggested_min)}</b> ({edit.min_days} кунлик заҳира).</>}
									</div>

									<div className="form-check form-switch mb-3">
										<input className="form-check-input" type="checkbox" id="isStock"
											checked={!!edit.is_stock_item}
											onChange={e => setEdit({ ...edit, is_stock_item: e.target.checked })} />
										<label className="form-check-label" htmlFor="isStock">
											<b>Омбордан сотилади</b> — қолдиқ назорат қилинади
										</label>
									</div>
									{/* Egasiga ochiq aytiladi: nega hammasiga yoqmaymiz */}
									<div className="text-muted mb-3" style={{ fontSize: 11.5 }}>
										Буюртмага кесиладиган маҳсулотда (профнастил, туникофон) белгиланмайди —
										уларда тайёр қолдиқ бўлмаслиги нормал ҳолат.
									</div>

									<div className="form-group mb-3">
										<label>Ҳамкор неча кунда келтиради</label>
										<input type="number" className="form-control" min="0"
											placeholder="2"
											value={edit.lead_days}
											onChange={e => setEdit({ ...edit, lead_days: e.target.value })} />
										<div className="text-muted" style={{ fontSize: 11.5 }}>
											Бўш қолдирилса 2 кун ҳисобланади. Мин. даража шундан келиб чиқади.
										</div>
									</div>

									<div className="form-group mb-3">
										<label>Мин. даража <span className="text-muted" style={{ fontSize: 12 }}>({edit.unit})</span></label>
										<input type="number" className="form-control" min="0"
											placeholder={edit.suggested_min !== null ? `Тавсия: ${son(edit.suggested_min)}` : ''}
											value={edit.min_quantity}
											onChange={e => setEdit({ ...edit, min_quantity: e.target.value })} />
										<div className="text-muted" style={{ fontSize: 11.5 }}>
											Бўш қолдирилса дастур ўзи ҳисоблайди ва сотув тезлиги ўзгарса
											даража ҳам ўзгаради. Сон ёзилса — <b>дастур унинг устидан ёзмайди</b>.
										</div>
									</div>

									<div className="form-group">
										<label>Бир марта заказ сони <span className="text-muted" style={{ fontSize: 12 }}>({edit.unit})</span></label>
										<input type="number" className="form-control" min="0"
											placeholder={edit.suggested_order !== null ? `Тавсия: ${son(edit.suggested_order)}` : ''}
											value={edit.order_qty}
											onChange={e => setEdit({ ...edit, order_qty: e.target.value })} />
									</div>
								</div>
								<div className="modal-footer">
									<button className="btn btn-outline-warning" onClick={() => setEdit(null)}>
										{t('cancel')}
									</button>
									<button className="btn btn-primary" onClick={saveEdit} disabled={saving}>
										{saving ? t('loading') : t('save')}
									</button>
								</div>
							</div>
						</div>
					</div>
				</>
			}
		</div>
	)
}
